import { ReactNode, useState } from 'react';
import { upperFirst } from 'lodash';
import styled from 'styled-components';

import {
  AdjudicationButton,
  DismissedToggleRow,
  IconGlyph,
  ViewDismissedButton,
} from 'components/inboundReferrals/AdjudicationButton';
import type { CitationViewerTarget } from 'components/shared/ai/CitationDocumentViewer';
import { useBesideCitationViewerWidth } from 'components/shared/ai/citationViewerLayout';
import SourceBadges from 'components/shared/ai/SourceBadges';
import { useCitationViewer, WORKFLOW_ATTACHABLE_MODEL } from 'components/shared/ai/useCitationViewer';
import Tooltip from 'components/shared/Tooltip';
import useIsMobile from 'hooks/useIsMobile';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import {
  AdjudicatedMatch,
  DISMISS,
  HIGH_COST_MEDS_REVIEWS,
  HighCostMedCandidate,
  HighCostMedReview,
  HighCostMedsAction,
  HighCostMedSource,
} from 'models/HighCostMedsAction';
import Workflow from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import DenialListEmpty from 'svg/DenialListEmpty';
import InfoIconOutline from 'svg/InfoIconOutline';
import RxIcon from 'svg/RxIcon';

import DetailSlideout from './DetailSlideout';

// The attachable model the packet's documents hang off, for the signed-URL click-through.

// Rendered for a value neither side supplied. This is a deliberate reversal of the kind's original
// omit-empty rule, and the reason is that the card became a COMPARISON. One-sided, a placeholder
// asked the reviewer to read meaning into an absence; two-sided, the absence IS the meaning — "the
// packet does not state a route, the contract does" is exactly what the reviewer needs to see, and
// dropping the line would leave the surviving value looking like it came from both.
const ABSENT = '—';

// Rendered for the contract side specifically when a matched med has no high-cost list detail
// for that field (e.g. a candidate with no tier). Distinct from ABSENT: this isn't a comparison
// gap between packet and contract, it's the list itself having nothing to say.
const NO_LIST_DETAIL = 'N/A';

// The one-line summary in the row body, for every state the row can be in. Counts come from the
// action model, which reads the matches off the committed workflow value — never action.outputs,
// which a dismissal replaces wholesale.
//
// The copy says "your organization’s" literally rather than substituting the acting client's name.
// That is design's wording, and it differs from DenialCheck's row on purpose, so this component
// deliberately takes no dependency on the profile.
export function HighCostMedsSummary({ action, workflow }: { action: HighCostMedsAction; workflow: Workflow }) {
  const { matched, dismissed, standing } = action.counts(workflow);

  // Before the result lands there is nothing to count, so this has to come first — a running check
  // has zero matches and would otherwise read as a clear one.
  if (action.isRunning) {
    return (
      <SummaryText data-testid='high-cost-meds-running'>Verifying potential matches for high cost meds</SummaryText>
    );
  }

  // An errored check presents exactly as DenialCheck's does: the row's own error status treatment
  // and nothing else — no body, no slideout, no Retry. CategoryCard already declines to render this
  // component for an errored action, so returning null is belt-and-braces — but it is load-bearing
  // belt: a failed run has zero matches, so falling through would reach the clear branch below and
  // assert that nothing matched, which is a different and false claim.
  if (action.isErrored) return null;

  if (matched === 0) {
    return (
      <SummaryText data-testid='high-cost-meds-result'>
        No medications matched your organization’s high cost med list
      </SummaryText>
    );
  }

  // Every match dismissed: the row is green, and the copy still has to say matches were FOUND —
  // "nothing matched" is a different fact and it isn't true here. So this keeps the flagged line's
  // opening and adds the dismissal as a trailing clause, rather than switching the sentence's
  // subject from what the check found to what a person did.
  if (standing === 0) {
    return (
      <SummaryText data-testid='high-cost-meds-result'>
        {matched === 1 ? '1 medication matched, dismissed' : `${matched} medications matched, all dismissed`}
      </SummaryText>
    );
  }

  // Flagged with some already dismissed. The dismissal HAS to appear here: without it the row is
  // byte-identical to an untouched one, so a reviewer who dismissed something, or who reloaded the
  // page afterwards, sees no evidence their work landed. Same shape as the all-dismissed line above,
  // so the three states read as one progression: "…matched" → "…matched, N dismissed" → "…matched,
  // all dismissed".
  if (dismissed > 0) {
    return (
      <SummaryText data-testid='high-cost-meds-flag'>
        {matched} medications matched, {dismissed} dismissed
      </SummaryText>
    );
  }

  // Flagged, nothing adjudicated yet — the count is of what the check found.
  return (
    <SummaryText data-testid='high-cost-meds-flag'>
      {matched} {matched === 1 ? 'medication' : 'medications'} matched your organization’s high cost med list
    </SummaryText>
  );
}

/**
 * The High Cost Medication Check adjudication slideout, shown on the COMPLETED action — the check
 * always completes when the AI reports, and adjudication happens afterwards on a completed action.
 *
 * Dismiss is the only verb. A medication is on the client's contract list or it isn't, several can
 * be, and none of them block the referral, so the only judgment worth recording is that one isn't a
 * real concern; an undismissed match already counts, which is why the row flags on its own.
 *
 * Each click is its own `save_progress` write carrying ONLY `high_cost_meds_reviews`. That differs
 * on purpose from the sex offender check's slideout, which echoes its screen result back on every
 * save because its model reads matches from `action.outputs`: this kind reads them from the
 * committed workflow value instead, and its `validate_progress!` REJECTS any other key.
 */
export default function HighCostMedsReview({
  action,
  workflow,
  open,
  onClose,
  onSaveProgress,
  isSavingProgress,
}: {
  action: HighCostMedsAction;
  workflow: Workflow;
  open: boolean;
  onClose: () => void;
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput) => void;
  isSavingProgress: boolean;
}) {
  // UX only: the API enforces the same rule and 403s regardless, so hiding Dismiss saves a
  // pointless click rather than protecting the write. Workflow-level edit is a superset; the
  // kind's own grant arrives on the action as `editPermissionName`.
  const { canEditAction } = useWorkflowPermissions();
  const canEdit = canEditAction(action, workflow);

  const [showDismissed, setShowDismissed] = useState(false);

  const matches = action.matches(workflow);
  const standing = matches.filter((m) => !m.dismissed);
  const dismissed = matches.filter((m) => m.dismissed);

  // Both verbs ride ONE entry per match, so a write to either has to carry the other forward —
  // rebuilding the entry from scratch would drop a confirmation when the reviewer dismisses, and
  // vice versa.
  const writeReview = (match: AdjudicatedMatch, changes: Partial<HighCostMedReview>) => {
    if (isSavingProgress) return;
    const others = action.reviews.filter((r) => r.matchFingerprint !== match.matchFingerprint);
    // Attribution is deliberately absent from both this type and this payload: api stamps who
    // confirmed and when, from the request's user, and strips whatever a caller sends. The stamp is
    // read back through the activity log, never through this value.
    const current: HighCostMedReview = {
      matchFingerprint: match.matchFingerprint,
      decision: match.dismissed ? DISMISS : null,
      confirmedHighCostMedicationListItemId: match.confirmedItemId,
      ...changes,
    };

    // An entry saying nothing is dropped rather than written as a row of nulls — the API accepts
    // both, and dropping keeps the recorded array to just the live decisions.
    const next: HighCostMedReview[] =
      current.decision === null && current.confirmedHighCostMedicationListItemId === null
        ? others
        : [...others, current];

    onSaveProgress(action.id, { [HIGH_COST_MEDS_REVIEWS]: next });
  };

  const toggleDismissal = (match: AdjudicatedMatch) =>
    writeReview(match, { decision: match.dismissed ? null : DISMISS });

  // Which contract row the patient is actually on. Never touches `decision`: confirming a
  // formulation says nothing about whether its cost is a concern, and only Dismiss clears the flag.
  const confirmCandidate = (match: AdjudicatedMatch, itemId: string) =>
    writeReview(match, { confirmedHighCostMedicationListItemId: itemId });

  const visible = showDismissed ? [...standing, ...dismissed] : standing;

  // A PDF source opens in the left-hand viewer at the medication's evidence; the slideout narrows
  // beside it rather than sitting underneath.
  const { openViewer, viewer } = useCitationViewer({ attachmentType: WORKFLOW_ATTACHABLE_MODEL, parentOpen: open });
  const slideoutWidth = useBesideCitationViewerWidth(undefined);

  return (
    <DetailSlideout
      open={open}
      onClose={onClose}
      width={slideoutWidth}
      title='High Cost Medication Check'
      // "your organization’s" literally, per design, rather than the acting client's name. Note the
      // row body abbreviates this to "high cost med list" while the slideout spells it out — that
      // difference is in the supplied copy for both, so it is kept rather than normalized.
      // With no matches the subtitle describes what the check DOES rather than counting a result —
      // there is no count to report, and the empty state below carries the outcome.
      subtitle={
        matches.length === 0
          ? 'Referencing against your organization’s high cost medication list'
          : `${matches.length} ${matches.length === 1 ? 'medication' : 'medications'} matched your organization’s high cost medication list`
      }>
      {/* No errored branch here on purpose: an errored check never opens (`opensReviewSlideout`),
          because it has no result to show — its failure and Try again sit on the row body instead. */}
      {matches.length === 0 ? (
        <EmptyState data-testid='high-cost-meds-no-matches'>
          {/* PLACEHOLDER ICON: the design's mark is a document-with-person carrying a green check.
              DenialListEmpty is the nearest existing green empty-state glyph and stands in until the
              real SVG lands — swap it here, nothing else changes. */}
          <DenialListEmpty width={40} height={40} />
          <EmptyTitle>No Matches Found for High Cost Medications</EmptyTitle>
          <EmptyBody>Olio did not identify any matches in your organization’s high cost medication list.</EmptyBody>
        </EmptyState>
      ) : (
        <>
          {dismissed.length > 0 && (
            <DismissedToggleRow>
              <ViewDismissedButton
                type='button'
                variant='outline-primary'
                onClick={() => setShowDismissed((shown) => !shown)}
                data-testid='high-cost-med-toggle-dismissed'>
                {showDismissed
                  ? `Hide Dismissed (${dismissed.length})`
                  : `View Dismissed Result${dismissed.length === 1 ? '' : 's'} (${dismissed.length})`}
              </ViewDismissedButton>
            </DismissedToggleRow>
          )}

          {/* The heading labels the STANDING cards, so it goes with them. Once every match is
              dismissed there are none left to label — and it stays gone when the dismissed cards are
              revealed, because the toggle above them already says what they are. */}
          <MatchedSection>
            {standing.length > 0 && <SectionHeading>Matched Medications</SectionHeading>}
            <Cards>
              {visible.map((match) => (
                <MatchCard
                  key={match.matchFingerprint}
                  match={match}
                  workflow={workflow}
                  canEdit={canEdit}
                  busy={isSavingProgress}
                  onToggle={() => toggleDismissal(match)}
                  onConfirm={(itemId) => confirmCandidate(match, itemId)}
                  onOpenSource={openViewer}
                />
              ))}
            </Cards>
          </MatchedSection>
        </>
      )}
      {viewer}
    </DetailSlideout>
  );
}

/**
 * One matched medication.
 *
 * A packet names an ingredient; a contract prices that ingredient under several brand combinations
 * at different tiers. So one medication can match several contract rows, and they arrive here as
 * candidates on a single card — one judgment, one control, one write. A match with a single
 * candidate is the ordinary case and renders the two-sided field grid unchanged.
 *
 * A fan-out reaches that same grid by being answered. Until the reviewer picks a row the card has no
 * single contract value to print, so the grid runs one-sided; picking one supplies it, and the card
 * resolves into the full comparison the instrument was built for.
 */
function MatchCard({
  match,
  workflow,
  canEdit,
  busy,
  onToggle,
  onConfirm,
  onOpenSource,
}: {
  match: AdjudicatedMatch;
  workflow: Workflow;
  canEdit: boolean;
  busy: boolean;
  onToggle: () => void;
  onConfirm: (itemId: string) => void;
  onOpenSource: (target: CitationViewerTarget) => void;
}) {
  // Whether there is anything to choose between. Keyed on the candidate COUNT, never on what has
  // been chosen: the chooser below has to survive its own answer, or picking a row would collapse
  // the list the reviewer picked it from.
  const fanOut = match.candidates.length > 1;

  // The one contract row this card speaks for — the lone candidate, or the one the reviewer picked
  // out of a fan-out. It supplies the contract side of every field, and it is undefined for exactly
  // as long as the question is open.
  const contractRow = fanOut
    ? match.candidates.find((c) => c.highCostMedicationListItemId === match.confirmedItemId)
    : match.candidates[0];
  const isMobile = useIsMobile();

  // `generic (brand)` is unrepresentable across candidates that disagree on the brand — which is the
  // entire reason they are candidates — so an unanswered fan-out is titled by the generic name alone
  // and the brands appear on the rows below. Answering it names the brand here too.
  const heading = contractRow ? contractIdentity(contractRow) : (match.candidates[0]?.genericName ?? '');

  // Dismiss answers the card, so the mock gives it the card's foot on a phone (Figma 12283:35554)
  // and the banner's right edge on a desktop. Those are two different parents, which is the one
  // thing CSS cannot do from a single node — and rendering it in both places would put the same
  // test id on screen twice.
  const dismiss = canEdit && (
    <AdjudicationButton
      type='button'
      variant='outline-gray'
      disabled={busy}
      onClick={onToggle}
      data-testid={`high-cost-med-dismiss-${match.matchFingerprint}`}>
      <IconGlyph aria-hidden>✕</IconGlyph>
      {match.dismissed ? 'Undismiss' : 'Dismiss'}
    </AdjudicationButton>
  );

  return (
    <Card data-testid='high-cost-med-card' $dismissed={match.dismissed}>
      <CardHeader $dismissed={match.dismissed}>
        <HeaderIcon $dismissed={match.dismissed}>
          <RxIcon width={18} height={18} />
        </HeaderIcon>
        <CardHeaderText data-testid='high-cost-med-card-heading'>{upperFirst(heading)}</CardHeaderText>
        {!isMobile && dismiss}
      </CardHeader>

      {/* The packet's side sits above any chooser, never behind it: a reviewer picking a formulation
          is comparing the packet's dose and route against each candidate's, so it has to be legible
          before the choice is made. The contract's values join it as soon as there is one row to
          take them from, which is the comparison the card was designed around. */}
      <FieldGrid>
        <Field
          testId='prescription'
          label='Prescription'
          packet={match.extracted.medication}
          contract={contractRow && contractIdentity(contractRow)}
          sides={contractRow ? 'both' : 'packet'}
        />
        {contractRow ? (
          <Field
            testId='cost-tier'
            label='Cost Tier'
            sides='contract'
            contract={contractRow.tierLabel}
            tooltip={tierTooltip(contractRow)}
          />
        ) : (
          // Cost tier is per candidate on an unanswered fan-out — the rows below each carry their
          // own, and one value here could only be a guess at which row applies.
          <GridSpacer />
        )}
        <Field
          testId='dosage'
          label='Dosage'
          packet={match.extracted.dosage}
          contract={contractRow?.dosage}
          sides={contractRow ? 'both' : 'packet'}
        />
        <Field
          testId='route'
          label='Route'
          packet={match.extracted.route}
          contract={contractRow?.route}
          sides={contractRow ? 'both' : 'packet'}
        />
        <Field
          testId='form'
          label='Form'
          packet={match.extracted.form}
          contract={contractRow?.form}
          sides={contractRow ? 'both' : 'packet'}
        />
        <GridSpacer />
        <Field
          testId='description'
          label='Description from packet'
          sides='packet'
          packet={quoted(match.extracted.description)}
        />
        <Sources sources={match.sources} workflow={workflow} onOpenSource={onOpenSource} />
      </FieldGrid>

      {fanOut && <CandidateSection match={match} canEdit={canEdit} busy={busy} onConfirm={onConfirm} />}

      {isMobile && dismiss && <DismissBar>{dismiss}</DismissBar>}
    </Card>
  );
}

/**
 * The candidate chooser.
 *
 * Choosing answers WHICH contract row the patient is on. It is not the Match verb this kind
 * declines: that would be a judgment about whether a hit is a real concern, and a list returns
 * facts about cost rather than candidates for identity. So a choice never clears the flag, is
 * never required, and a reviewer can dismiss without ever choosing.
 *
 * Selecting a row writes it. There is no staged selection and no commit button, because the two
 * reasons to stage one do not hold here: the write is a single field, and picking another row
 * replaces it outright, so nothing about reading the second option costs the reviewer anything.
 * A commit button would only ask them to record the same decision twice.
 *
 * The list does not collapse once a row is chosen. The chosen row is the selected one — blue, with
 * its radio filled — and the rest stay legible beside it, because a choice the packet did not
 * settle is one a reviewer may revisit.
 */
function CandidateSection({
  match,
  canEdit,
  busy,
  onConfirm,
}: {
  match: AdjudicatedMatch;
  canEdit: boolean;
  busy: boolean;
  onConfirm: (itemId: string) => void;
}) {
  return (
    <CandidateBlock data-testid='high-cost-med-candidates'>
      {/* No cost tier key here. Each candidate row prints its own tier inline, so the scale a
          reviewer needs is beside the value it grades rather than behind a glyph on the heading. */}
      <SectionHeading>{match.candidates.length} Potential Matches</SectionHeading>
      <CandidateHint>
        {upperFirst(match.candidates[0]?.genericName ?? 'This medication')} appears {match.candidates.length} times on
        your organization’s high-cost list under different brand combinations. Review them below:
      </CandidateHint>
      <CandidateList role='radiogroup' aria-label='Potential contract matches'>
        {match.candidates.map((candidate) => {
          const id = candidate.highCostMedicationListItemId;
          const selected = match.confirmedItemId === id;
          // The row is a label around its radio, so the whole row is already the radio's hit target
          // and a click anywhere in it arrives here. Re-picking the row already recorded is dropped
          // rather than resent: api answers an unchanged confirmation by carrying the existing stamp
          // forward, so the request would write nothing and narrate nothing.
          const choose = () => {
            if (!canEdit || busy || selected) return;
            onConfirm(id);
          };
          return (
            <CandidateOption
              key={id}
              $selected={selected}
              $disabled={!canEdit || busy}
              data-testid={`high-cost-med-candidate-${id}`}>
              <CandidateRadio
                type='radio'
                name={`candidate-${match.matchFingerprint}`}
                checked={selected}
                disabled={!canEdit || busy}
                onChange={choose}
              />
              <CandidateText>
                <CandidateName>{candidateIdentity(candidate)}</CandidateName>
                <CandidateDetail>{candidateSummary(candidate)}</CandidateDetail>
              </CandidateText>
            </CandidateOption>
          );
        })}
      </CandidateList>
    </CandidateBlock>
  );
}

// Which sides a field has. `both` is the default and the interesting case: the packet's value over
// the contract's, either of which may be silent. The two exceptions are structural rather than
// data-driven, which is why they are declared per call site instead of inferred from a nullish
// value — a packet that simply didn't state a dosage must still render its em-dash.
type FieldSides = 'both' | 'contract' | 'packet';

/**
 * One field, rendered as the packet's value over the contract's.
 *
 * Every configurable item field is two-sided, because an admin configures it AND olio-ai extracts
 * it, and either side can be silent: a list item need only carry a generic name, and the packet may
 * not state a route. The silent side shows an em-dash so the reader can never mistake one side's
 * value for both — the absence is the information.
 */
function Field({
  testId,
  label,
  packet,
  contract,
  tooltip,
  sides = 'both',
}: {
  testId: string;
  label: string;
  packet?: string | null;
  contract?: string | null;
  tooltip?: ReactNode;
  sides?: FieldSides;
}) {
  return (
    <FieldBlock data-testid={`high-cost-med-field-${testId}`}>
      <FieldLabel>
        {label}
        {tooltip && (
          <Tooltip text={tooltip} maxWidth={320}>
            <InfoGlyph aria-label={`${label} key`}>
              <InfoIconOutline width={14} height={14} fill={colors.black50} />
            </InfoGlyph>
          </Tooltip>
        )}
      </FieldLabel>
      {sides !== 'contract' && <PacketValue>{packet || ABSENT}</PacketValue>}
      {sides === 'contract' && <ContractValue>{contract || NO_LIST_DETAIL}</ContractValue>}
      {sides === 'both' && <ContractValue>{`High-cost list: ${contract || NO_LIST_DETAIL}`}</ContractValue>}
    </FieldBlock>
  );
}

/**
 * Each source as a numbered citation badge, the same chip the clinical assessment draws: hovering
 * shows the cited page with the evidence outlined, and clicking a PDF opens it in the document viewer
 * at that evidence. A source that isn't a PDF keeps opening in a new tab.
 */
function Sources({
  sources,
  workflow,
  onOpenSource,
}: {
  sources: HighCostMedSource[];
  workflow: Workflow;
  onOpenSource: (target: CitationViewerTarget) => void;
}) {
  return (
    <FieldBlock data-testid='high-cost-med-field-sources'>
      <FieldLabel>Sources</FieldLabel>
      {sources.length === 0 ? (
        <PacketValue>{ABSENT}</PacketValue>
      ) : (
        <SourceBadgeRow data-testid='high-cost-med-source-list'>
          <SourceBadges sources={sources} attachments={workflow.attachments} onOpen={onOpenSource} />
        </SourceBadgeRow>
      )}
    </FieldBlock>
  );
}

// The contract's own identity for a row: generic name, with the brand in parentheses when the admin
// recorded one. Generic name is the only field required on a list item, so it is the one value a
// card can always show.
function contractIdentity(candidate: HighCostMedCandidate): string {
  const generic = candidate.genericName ?? '';
  return candidate.brandName ? `${generic} (${candidate.brandName})` : generic;
}

// A candidate row's title. Capitalised here rather than at the call site because every row in a
// chooser needs it and the generic names arrive lower-cased from the contract.
function candidateIdentity(candidate: HighCostMedCandidate): string {
  return upperFirst(contractIdentity(candidate));
}

// The one line that distinguishes candidate rows from each other — dose, route and cost tier are
// what differs between a contract's brand combinations. A full field grid per candidate would be
// four times the height for the same information.
function candidateSummary(candidate: HighCostMedCandidate): string {
  const parts = [candidate.dosage, candidate.route, candidate.tierLabel].filter(Boolean);
  return parts.length > 0 ? parts.join(' • ') : ABSENT;
}

/**
 * The whole cost tier key, with this medication's rung marked.
 *
 * Restating the one tier already printed beside the icon would tell the reviewer nothing: `$$$` only
 * means something against the scale it came from — whether it is the top of a three-rung key or the
 * middle of a five-rung one is the actual information.
 *
 * Read from the FROZEN key on the result, never fetched live: `GET /high_cost_medication_tiers`
 * serves the client's current active list, which after an annual contract rollover is a different
 * key entirely from the one this check ran against.
 */
function tierTooltip(candidate: HighCostMedCandidate): ReactNode {
  if (!candidate.tierLabel && !candidate.tierDescription) return undefined;

  // A result recorded before the key was frozen carries none, so fall back to this row's own tier
  // rather than rendering an empty tooltip.
  const key =
    candidate.tierKey.length > 0
      ? candidate.tierKey
      : [{ label: candidate.tierLabel, description: candidate.tierDescription, position: candidate.tierPosition }];

  return (
    <TooltipBody data-testid='high-cost-med-tier-key'>
      {/* Every rung renders identically — the medication's own tier is already printed beside the
          icon that opened this, so marking it again here adds emphasis without adding information.
          The tooltip's job is to show the scale, not to restate the value. */}
      {key.map((tier, index) => (
        <TierRow key={`${tier.label}-${index}`} data-testid={`high-cost-med-tier-key-row-${tier.position ?? index}`}>
          <TierRowLabel>{tier.label}</TierRowLabel>
          {tier.description && <span>{tier.description}</span>}
        </TierRow>
      ))}
    </TooltipBody>
  );
}

function quoted(value: string | null | undefined): string | null {
  return value ? `“${value}”` : null;
}

const SummaryText = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const SectionHeading = styled(BodySmallBold)`
  color: ${colors.black100};
`;

// The heading and the cards it labels are one band, so the slideout body's 24px gap falls between
// this section and what precedes it rather than between a heading and its own cards.
const MatchedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Every band inside a match card is inset by this much, so the header, the field grid and the
// candidate chooser line up on one left edge instead of three.
const CARD_INSET = '24px';

const Card = styled.div<{ $dismissed: boolean }>`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
  opacity: ${({ $dismissed }) => ($dismissed ? 0.6 : 1)};
`;

// Tighter vertically than the card's other bands — it is a banner, and its own background gives it
// height — but on the same 24px horizontal inset, so its text starts where the field labels do.
const CardHeader = styled.div<{ $dismissed: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px ${CARD_INSET};
  background: ${({ $dismissed }) => ($dismissed ? colors.black5 : colors.red10)};
`;

const HeaderIcon = styled.span<{ $dismissed: boolean }>`
  display: inline-flex;
  color: ${({ $dismissed }) => ($dismissed ? colors.black50 : colors.red100)};
`;

const CardHeaderText = styled(BodySmallBold)`
  flex: 1;
  color: ${colors.black100};
`;

const InfoGlyph = styled.span`
  display: inline-flex;
  margin-left: 4px;
  cursor: help;
`;

// The phone's home for Dismiss: a band across the card's foot, the button spanning it at the 24px
// the mock draws (Figma 12283:35554). Rendered only on a phone, so none of this is behind a media
// query — the tap area comes from AdjudicationButton's own rule, which grows the area not the box.
const DismissBar = styled.div`
  display: flex;
  padding: ${CARD_INSET};
  border-top: 1px solid ${colors.black10};

  button {
    flex: 1;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 24px;
  padding: ${CARD_INSET};
`;

// Keeps Description from packet + Sources on their own row, as the mock draws them, now that Form
// has joined the grid. Also stands in for Cost Tier on a fan-out, where the tier belongs to each
// candidate rather than to the card.
const GridSpacer = styled.div``;

// The chooser, below the packet's own values and separated from them by a rule.
const CandidateBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${CARD_INSET};
  border-top: 1px solid ${colors.black10};
`;

// A step below its 14px heading, matching the label-then-value sizing the field grid uses.
const CandidateHint = styled(BodySmall)`
  font-size: ${({ theme }) => theme.textStyles.label.fontSize};
  line-height: ${({ theme }) => theme.textStyles.label.lineHeight};
  color: ${colors.black75};
`;

// 24px between the hint and the list it introduces, per the mock. The block already gaps its
// children by 8px, so this adds the remainder rather than restating the total.
const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const CandidateOption = styled.label<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  /* The mock's 78px row, reached by padding rather than a height so a wrapping name or summary
     grows the row instead of overflowing it: 20px + 20px padding + 2px border over two 16px lines
     of text and their 4px gap. */
  padding: 20px 16px;
  border-radius: 4px;
  /* The label IS the radio's hit target, so it has to agree with the input about whether the row
     takes a click — a pointer over a row that refuses one is the only thing on screen still
     claiming it is pickable. :disabled can't reach a label, hence the prop. */
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black10)};
  background: ${({ $selected }) => ($selected ? colors.blue5 : colors.white)};
`;

const CandidateRadio = styled.input`
  margin: 2px 0 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const CandidateText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

// A candidate row's text is uniformly 12px — the name carries the weight, not a larger size. The
// row's 78px height is padded around these two 16px lines, so changing either size moves both.
const CandidateName = styled(BodySmallBold)`
  font-size: ${({ theme }) => theme.textStyles.label.fontSize};
  line-height: ${({ theme }) => theme.textStyles.label.lineHeight};
  color: ${colors.black100};
  overflow-wrap: anywhere;
`;

const CandidateDetail = styled(CandidateName)`
  font-weight: normal;
  color: ${colors.black50};
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FieldLabel = styled(BodySmallBold)`
  display: flex;
  align-items: center;
  color: ${colors.black100};
`;

// Values sit a step below their 14px label rather than matching it, so a field reads as one
// label-then-value unit instead of two lines of equal weight.
const PacketValue = styled(BodySmall)`
  font-size: ${({ theme }) => theme.textStyles.label.fontSize};
  line-height: ${({ theme }) => theme.textStyles.label.lineHeight};
  color: ${colors.black100};
  overflow-wrap: anywhere;
`;

const ContractValue = styled(PacketValue)`
  color: ${colors.black50};
`;

const SourceBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const TooltipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// One rung of the key. Label and description are separate columns so the labels line up down the
// left edge and the scale reads as a scale rather than as four sentences.
const TierRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
`;

const TierRowLabel = styled.span`
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  padding: 48px 24px;
  border-radius: 4px;
  background: ${colors.green5};
`;

const EmptyTitle = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black100};
  margin-top: 8px;
`;

const EmptyBody = styled(BodySmall)`
  color: ${colors.black75};
`;
